import type { AYamlConfig } from 'codesearch-backend'
import { createSignal, For, Show } from 'solid-js'
import { Badge } from './ui/Badge.tsx'

interface AYamlViewProps {
  config: AYamlConfig
}

export function AYamlView(props: AYamlViewProps) {
  const cfg = () => props.config
  const ci = () => cfg().ci
  const flows = () => ci()?.flows ?? {}
  const flowKeys = () => Object.keys(flows())

  const [selectedFlow, setSelectedFlow] = createSignal<string | null>(null)

  const activeFlowKey = () => selectedFlow() ?? flowKeys()[0] ?? null
  const activeFlow = () => (activeFlowKey() ? flows()[activeFlowKey()!] : null)

  const sandboxOwner = () => {
    const runtime = ci()?.runtime
    if (!runtime) return null
    if (runtime.sandbox?.owner) return runtime.sandbox.owner
    if (runtime['sandbox-owner']) return runtime['sandbox-owner']
    if (runtime.sandbox_owner) return runtime.sandbox_owner
    return null
  }

  const sandboxTags = () => ci()?.runtime?.sandbox?.tags ?? []
  const sandboxHints = () => ci()?.runtime?.sandbox?.hints ?? []

  return (
    <div class="h-full overflow-auto bg-background p-6 space-y-6">
      {/* Top Banner / Summary Card */}
      <div class="border border-border rounded-xl bg-card p-5 shadow-sm space-y-4">
        <div class="flex flex-wrap items-start justify-between gap-4">
          <div class="space-y-1">
            <div class="flex items-center gap-2">
              <span class="i-lucide-file-cog size-6 text-primary" />
              <h1 class="text-xl font-semibold tracking-tight">{cfg().title || 'a.yaml Specification'}</h1>
            </div>
            <Show when={cfg().service}>
              <div class="flex items-center gap-2 text-xs text-muted-foreground">
                <span class="font-medium">ABC Service:</span>
                <Badge variant="outline" class="text-xs font-mono">{cfg().service}</Badge>
              </div>
            </Show>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <Show when={ci()?.secret}>
              <Badge variant="secondary" class="gap-1.5 px-2.5 py-1 text-xs font-mono">
                <span class="i-lucide-key size-3.5 text-amber-500" />
                Secret:
                {' '}
                {ci()!.secret}
              </Badge>
            </Show>
            <Show when={sandboxOwner()}>
              <Badge variant="secondary" class="gap-1.5 px-2.5 py-1 text-xs">
                <span class="i-lucide-box size-3.5 text-blue-500" />
                Sandbox Owner:
                {' '}
                {sandboxOwner()}
              </Badge>
            </Show>
          </div>
        </div>

        {/* Runtime tags / hints */}
        <Show when={sandboxTags().length > 0 || sandboxHints().length > 0}>
          <div class="flex flex-wrap items-center gap-2 border-t border-border/50 pt-3 text-xs">
            <span class="text-muted-foreground font-medium">Runtime Tags & Hints:</span>
            <For each={sandboxTags()}>
              {tag => <Badge variant="outline" class="border-blue-500/20 bg-blue-500/10 text-blue-600 dark:text-blue-400">{tag}</Badge>}
            </For>
            <For each={sandboxHints()}>
              {hint => <Badge variant="outline" class="border-purple-500/20 bg-purple-500/10 text-purple-600 dark:text-purple-400">{hint}</Badge>}
            </For>
          </div>
        </Show>
      </div>

      {/* Grid: Releases & Actions */}
      <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
        {/* Releases */}
        <div class="border border-border rounded-xl bg-card p-5 shadow-sm space-y-3">
          <div class="flex items-center gap-2 text-sm text-foreground font-medium">
            <span class="i-lucide-rocket size-4 text-emerald-500" />
            <h2>Releases</h2>
            <Badge variant="secondary" size="sm">{Object.keys(ci()?.releases ?? {}).length}</Badge>
          </div>

          <Show
            when={Object.keys(ci()?.releases ?? {}).length > 0}
            fallback={<p class="text-xs text-muted-foreground italic">No release specs defined</p>}
          >
            <div class="space-y-2">
              <For each={Object.entries(ci()?.releases ?? {})}>
                {([key, rel]) => (
                  <div class="flex items-center justify-between border border-border/60 rounded-lg bg-muted/20 p-3 text-xs">
                    <div>
                      <div class="text-foreground font-semibold">{rel.title || key}</div>
                      <div class="mt-0.5 text-2xs text-muted-foreground font-mono">
                        Target flow:
                        {' '}
                        <span class="text-primary">{rel.flow}</span>
                      </div>
                    </div>
                    <Show when={rel.stage}>
                      <Badge variant="outline" size="sm">{rel.stage}</Badge>
                    </Show>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </div>

        {/* Actions / Triggers */}
        <div class="border border-border rounded-xl bg-card p-5 shadow-sm space-y-3">
          <div class="flex items-center gap-2 text-sm text-foreground font-medium">
            <span class="i-lucide-zap size-4 text-amber-500" />
            <h2>Actions & Triggers</h2>
            <Badge variant="secondary" size="sm">{Object.keys(ci()?.actions ?? {}).length}</Badge>
          </div>

          <Show
            when={Object.keys(ci()?.actions ?? {}).length > 0}
            fallback={<p class="text-xs text-muted-foreground italic">No automatic trigger actions defined</p>}
          >
            <div class="space-y-2">
              <For each={Object.entries(ci()?.actions ?? {})}>
                {([key, act]) => (
                  <div class="border border-border/60 rounded-lg bg-muted/20 p-3 text-xs space-y-1.5">
                    <div class="flex items-center justify-between">
                      <span class="text-foreground font-semibold">{key}</span>
                      <span class="text-2xs text-muted-foreground font-mono">
                        Flow:
                        {' '}
                        <span class="text-primary">{act.flow}</span>
                      </span>
                    </div>
                    <div class="flex flex-wrap items-center gap-1.5">
                      <For each={act.triggers || []}>
                        {(trig) => {
                          const onEvents = Array.isArray(trig.on) ? trig.on : [trig.on]
                          return (
                            <For each={onEvents}>
                              {ev => (
                                <Badge variant="default" size="sm" class="border-amber-500/30 bg-amber-500/15 text-amber-700 dark:text-amber-300">
                                  on:
                                  {ev}
                                </Badge>
                              )}
                            </For>
                          )
                        }}
                      </For>
                    </div>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </div>
      </div>

      {/* Flow & Job Breakdown */}
      <div class="border border-border rounded-xl bg-card p-5 shadow-sm space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3">
          <div class="flex items-center gap-2 text-sm text-foreground font-medium">
            <span class="i-lucide-workflow size-4 text-blue-500" />
            <h2>
              CI Flows (
              {flowKeys().length}
              )
            </h2>
          </div>

          {/* Flow selector tabs */}
          <div class="flex flex-wrap items-center gap-1.5">
            <For each={flowKeys()}>
              {fk => (
                <button
                  type="button"
                  onClick={() => setSelectedFlow(fk)}
                  class={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    activeFlowKey() === fk
                      ? 'bg-primary text-primary-foreground shadow-sm'
                      : 'bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  {fk}
                </button>
              )}
            </For>
          </div>
        </div>

        {/* Selected Flow Details */}
        <Show
          when={activeFlow()}
          fallback={<p class="text-xs text-muted-foreground italic">No flows defined in this configuration</p>}
        >
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <h3 class="text-sm font-semibold">{activeFlow()?.title || activeFlowKey()}</h3>
            </div>

            {/* Jobs */}
            <Show
              when={activeFlow()?.jobs}
              fallback={<p class="text-xs text-muted-foreground italic">No specific jobs listed for this flow</p>}
            >
              <div class="grid grid-cols-1 gap-3">
                <For each={Array.isArray(activeFlow()!.jobs) ? (activeFlow()!.jobs as any[]) : Object.entries(activeFlow()!.jobs || {})}>
                  {(entry) => {
                    const [jobKey, job] = Array.isArray(activeFlow()!.jobs)
                      ? [(entry as any).task || 'Job', entry as any]
                      : [entry[0], entry[1] as any]

                    return (
                      <div class="border border-border/80 rounded-lg bg-background p-4 text-xs space-y-2.5">
                        <div class="flex flex-wrap items-center justify-between gap-2">
                          <div class="flex items-center gap-2 text-foreground font-medium">
                            <span class="i-lucide-cpu size-4 text-muted-foreground" />
                            <span>{job.title || jobKey}</span>
                            <Show when={job.task}>
                              <Badge variant="outline" size="sm" class="text-2xs font-mono">{job.task}</Badge>
                            </Show>
                          </div>

                          <div class="flex items-center gap-2">
                            <Show when={job.attempts}>
                              <Badge variant="secondary" size="sm">
                                Retries:
                                {job.attempts}
                              </Badge>
                            </Show>
                            <Show when={job.timeout}>
                              <Badge variant="secondary" size="sm">
                                Timeout:
                                {job.timeout}
                              </Badge>
                            </Show>
                          </div>
                        </div>

                        {/* Needs / Dependencies */}
                        <Show when={job.needs && job.needs.length > 0}>
                          <div class="flex items-center gap-1.5 text-2xs text-muted-foreground">
                            <span>Depends on:</span>
                            <For each={job.needs}>
                              {dep => <Badge variant="outline" size="sm" class="text-2xs font-mono">{dep}</Badge>}
                            </For>
                          </div>
                        </Show>

                        {/* Commands / Script */}
                        <Show when={job.cmds || job.script}>
                          <div class="rounded-md bg-muted/40 p-2.5 text-2xs font-mono space-y-1">
                            <For each={job.cmds || (Array.isArray(job.script) ? job.script : [job.script])}>
                              {cmd => (
                                <div class="text-foreground/90">
                                  $
                                  {cmd}
                                </div>
                              )}
                            </For>
                          </div>
                        </Show>

                        {/* Env vars */}
                        <Show when={job.env && Object.keys(job.env).length > 0}>
                          <div class="flex flex-wrap items-center gap-1.5">
                            <span class="text-2xs text-muted-foreground">Env:</span>
                            <For each={Object.entries(job.env)}>
                              {([k, v]) => (
                                <span class="rounded bg-muted px-1.5 py-0.5 text-2xs text-muted-foreground font-mono">
                                  {k}
                                  =
                                  {String(v)}
                                </span>
                              )}
                            </For>
                          </div>
                        </Show>
                      </div>
                    )
                  }}
                </For>
              </div>
            </Show>
          </div>
        </Show>
      </div>
    </div>
  )
}
